import { z } from "zod";
import { FollowUpPromiseTextSchema } from "../../../skills/leadcue-reply/scripts/lib/follow-up-promise.ts";
import type {
  GoalCompletion,
  GoalCorrection,
  PlanStep,
  StepEvidence,
  WorkPlan,
} from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";

/*
 * The per-conversation event record (docs/2026-09-19-inbox-direction-a-design.md, section 1).
 *
 * The record is audit and the inbox's source. It is never a stage input: nothing here is read by
 * drafting. `evidence` points at the record that proves an event and never holds a body, an address,
 * a token or a signed URL.
 */

const key = z.string().min(1).max(400);
const ref = z.string().min(1).max(400);
const stepId = z.string().min(1).max(200);
const revision = z.number().int().nonnegative();
const instant = z.iso.datetime({ offset: true });

const EventActorSchema = z.strictObject({
  type: z.enum(["lead", "seller", "rep", "agent", "system", "provider", "external"]),
  id: z.string().min(1).max(200).nullable(),
});
export type EventActor = z.infer<typeof EventActorSchema>;

const EventEvidenceSchema = z.strictObject({
  kind: z.enum([
    "bison_message",
    "draft_revision",
    "command",
    "run",
    "provider_receipt",
    "qualification_decision",
    "context_record",
    "ledger_stop",
    "outbox",
    "intake_reply",
  ]),
  ref,
});
type EventEvidence = z.infer<typeof EventEvidenceSchema>;

/** Set only by the backfill: how much of the original moment survived to be mapped. */
const backfill = z
  .strictObject({ precision: z.enum(["exact", "snapshot", "current_state_only"]) })
  .optional();

const common = {
  eventKey: key,
  actor: EventActorSchema,
  occurredAt: instant,
  evidence: EventEvidenceSchema,
  providerLeadId: z.number().int().positive().nullable(),
};

function event<const K extends string, const D extends z.ZodRawShape>(kind: K, detail: D) {
  return z.strictObject({
    ...common,
    kind: z.literal(kind),
    detail: z.strictObject({ ...detail, backfill }),
  });
}

const step = { stepId };
const operation = { stepId, operationId: ref };

export const ConversationEventSchema = z.discriminatedUnion("kind", [
  event("message.inbound", { messageId: ref }),
  event("message.outbound", { messageId: ref }),
  event("disposition.set", { disposition: z.string().min(1).max(80), reason: ref.nullable() }),
  event("qualification.decided", {
    outcome: z.enum(["qualified", "not_qualified", "error"]),
    source: z.string().min(1).max(80),
  }),
  event("reply.classified", {
    stance: z.string().min(1).max(80),
    asks: z.array(z.string().min(1).max(80)).max(20),
    route: z.string().min(1).max(80).nullable(),
  }),
  event("question.asked", { ...step, keys: z.array(ref).min(1).max(20) }),
  event("question.answered", { ...step, uploadIds: z.array(ref).max(20) }),
  event("promise.created", {
    promiseId: ref,
    questionKey: ref,
    question: FollowUpPromiseTextSchema,
    topic: z.string().min(1).max(48),
    checker: ref,
    followUpOn: z.iso.date(),
    commitmentPhrase: FollowUpPromiseTextSchema,
  }),
  event("promise.answer_ready", { promiseId: ref }),
  event("promise.kept", { promiseId: ref }),
  event("draft.checked", {
    revision,
    verdict: z.string().min(1).max(80),
    ruleIds: z.array(ref).max(200),
  }),
  event("draft.generated", {
    draftRevision: revision,
    /** Null only for a backfilled draft: generated rows never stored the run that wrote them. */
    runId: ref.nullable(),
    answersMessageId: ref.nullable(),
  }),
  event("draft.withheld", { state: z.string().min(1).max(80), ruleIds: z.array(ref).max(200) }),
  event("draft.edited", { ...step, draftRevision: revision }),
  event("step.approved", {
    ...step,
    stepRevision: revision,
    draftRevision: revision.nullable(),
  }),
  event("step.skipped", step),
  event("step.revised", { ...step, stepRevision: revision }),
  event("step.queued", operation),
  event("step.reopened", step),
  event("step.completed", {
    ...step,
    evidence: z.enum(["rep_claim", "provider_receipt", "clock"]),
  }),
  event("step.invalidated", { ...step, cause: z.string().min(1).max(80) }),
  event("approval.refused_stale", { observed: ref }),
  event("reply.reported_sent", { ...step, draftRevision: revision }),
  event("reply.sent_observed", { messageId: ref, draftRevision: revision }),
  event("effect.started", operation),
  event("effect.executed", { ...operation, providerId: ref }),
  event("effect.uncertain", { ...operation, failureCode: ref.nullable() }),
  event("effect.cancelled", operation),
  event("suppression.recorded", {
    basis: z.string().min(1).max(80),
    reach: z.string().min(1).max(80),
    source: z.string().min(1).max(80),
  }),
  event("goal.completed", {
    source: z.enum(["hubspot_readonly", "rep_claim"]),
    startsAt: instant.nullable(),
  }),
  event("outcome.reviewed", { kind: z.enum(["no_response", "deck_only", "active"]) }),
  event("notification.sent", { channel: z.string().min(1).max(80) }),
  event("correction", { supersedes: key }),
]);
export type ConversationEvent = z.infer<typeof ConversationEventSchema>;
export type ConversationEventKind = ConversationEvent["kind"];

/**
 * Events that ask for the rep's attention (design section 3.4): a conversation is unread for a user
 * when one of these is newer than that user's cursor. Housekeeping never lights the dot.
 */
export const ATTENTION_KINDS = [
  "message.inbound",
  "question.asked",
  "draft.generated",
  "draft.withheld",
  "effect.uncertain",
  "approval.refused_stale",
  "goal.completed",
] as const satisfies readonly ConversationEventKind[];

/** As read back: the recording order is the unread cursor, so it travels with the event. */
export const StoredConversationEventSchema = z.intersection(
  ConversationEventSchema,
  z.object({ id: z.number().int().positive(), recordedAt: instant }),
);
export type StoredConversationEvent = z.infer<typeof StoredConversationEventSchema>;

/**
 * Keys for events that record a fact carrying its own identity: an approval's instant, a draft
 * revision, an operation, a decision, a receipt. Live writers and the backfill both build keys
 * here, so a fact recorded live and found again by the backfill is one event, not two.
 * Transitions without their own identity (skip, revise, reopen, invalidate) are keyed by the change
 * that caused them.
 */
export const factKeys = {
  approved: (stepId: string, at: string) => `approval:${stepId}:${at}`,
  draft: (revision: number, kind: "draft.generated" | "draft.edited") =>
    `draft:r${revision}:${kind}`,
  effect: (
    operationId: string,
    phase: "queued" | "started" | "uncertain" | "executed" | "cancelled",
  ) => `effect:${operationId}:${phase}`,
  completion: (stepId: string, at: string, kind: "question.answered" | "step.completed") =>
    `${kind}:${stepId}:${at}`,
  promise: (promiseId: string, phase: "created" | "answer-ready" | "kept") =>
    `promise:${promiseId}:${phase}`,
  sent: (decisionId: string) => `sent:${decisionId}`,
  outcome: (decisionId: string) => `outcome:${decisionId}`,
  goal: (evidenceId: string) => `goal:${evidenceId}`,
  goalCorrection: (decisionId: string) => `goal-correction:${decisionId}`,
  message: (messageId: string) => `message:${messageId}`,
  notification: (outboxId: string) => `notification:${outboxId}`,
  qualification: (decisionId: string) => `qualification:${decisionId}`,
} as const;

/**
 * A booking as an event. A HubSpot receipt is the provider's, observed when Replies read it; a Mark
 * booked is the rep's, at the tap, proven by the command. Live writers and the backfill both build
 * it here, so the two agree on key, actor and evidence.
 */
export function goalCompletedEvent(completion: GoalCompletion) {
  const common = {
    eventKey: factKeys.goal(completion.evidenceId),
    kind: "goal.completed" as const,
    providerLeadId: null,
  };
  return completion.source === "rep_claim"
    ? {
        ...common,
        actor: { type: "rep" as const, id: completion.actorId },
        occurredAt: new Date(Date.parse(completion.claimedAt)).toISOString(),
        evidence: { kind: "command" as const, ref: completion.evidenceId },
        detail: { source: completion.source, startsAt: null },
      }
    : {
        ...common,
        actor: { type: "provider" as const, id: "hubspot" },
        occurredAt: new Date(Date.parse(completion.observedAt)).toISOString(),
        evidence: { kind: "provider_receipt" as const, ref: completion.evidenceId },
        detail: { source: completion.source, startsAt: completion.startsAt },
      };
}

/** An undone Mark booked: a correction naming the booking event it supersedes. */
export function goalCorrectionEvent(correction: GoalCorrection) {
  return {
    eventKey: factKeys.goalCorrection(correction.decisionId),
    kind: "correction" as const,
    actor: { type: "rep" as const, id: correction.actorId },
    occurredAt: new Date(Date.parse(correction.at)).toISOString(),
    evidence: { kind: "command" as const, ref: correction.decisionId },
    providerLeadId: null,
    detail: { supersedes: factKeys.goal(correction.supersedes) },
  };
}

/**
 * What the plan diff cannot see: who asked for the change, when, under which record, and why any
 * step it did not name was invalidated. `key` makes every event from one change deterministic, so a
 * replayed change adds nothing.
 */
export type ChangeCause = {
  key: string;
  actor: EventActor;
  occurredAt: string;
  evidence: EventEvidence;
  invalidation: string;
};

/**
 * A projection rebuilds the plan from a run or a held reply rather than applying a command. Its new
 * steps are proposals, and a draft it replaces was generated, not edited.
 */
export type ProjectionContext = {
  runId: string | null;
  /** The message the generated draft answers: the reply that started the run. */
  answersMessageId: string | null;
  /** Why a step the projection no longer proposes stopped being current. */
  removed: string;
};

/** Steps whose end is already recorded; dropping them from a rebuilt plan loses nothing. */
const SETTLED = new Set<PlanStep["state"]>(["completed", "reported", "skipped", "invalidated"]);

/**
 * Every event a plan change implies, derived from the plan before and after it.
 *
 * Deriving from the diff rather than from the command means a new command cannot forget its event.
 * It also fails closed: a step state change with no mapping throws, and the change that caused it
 * then cannot commit, because every caller writes events inside the plan's own transaction.
 */
export function planEvents(
  before: WorkPlan | null,
  after: WorkPlan,
  cause: ChangeCause,
  options: { projection?: ProjectionContext } = {},
): ConversationEvent[] {
  const projection = options.projection ?? null;
  const events: ConversationEvent[] = [];
  const base = (subject: string, kind: string, overrides: EventOverrides = {}) => ({
    eventKey: overrides.key ?? `${cause.key}:${kind}:${subject}`,
    actor: overrides.actor ?? cause.actor,
    occurredAt: overrides.occurredAt ?? cause.occurredAt,
    evidence: overrides.evidence ?? cause.evidence,
    providerLeadId: null,
  });
  const previousSteps = new Map(
    (before?.steps ?? []).map((candidate) => [candidate.id, candidate]),
  );
  const appendedHistory = after.history.slice(before?.history.length ?? 0);

  for (const current of after.steps) {
    const prior = previousSteps.get(current.id);
    previousSteps.delete(current.id);
    const id = current.id;
    if (!prior) {
      // A command never adds a step, so an addition outside a projection is unaccounted for.
      if (!projection) throw new Error("unrecorded_step_addition");
      const proposed = proposalEvent(after, current, projection, base);
      if (proposed) events.push(proposed);
      continue;
    }
    const approvalChanged =
      current.approval !== null &&
      JSON.stringify(current.approval) !== JSON.stringify(prior.approval);
    const revised = current.revision !== prior.revision;
    if (current.state === prior.state && !approvalChanged && !revised) continue;

    if (current.state === "approved" && current.approval && approvalChanged) {
      events.push({
        ...base(id, "step.approved", {
          key: factKeys.approved(id, current.approval.at),
          actor: { type: "rep", id: current.approval.actorId },
          occurredAt: current.approval.at,
        }),
        kind: "step.approved",
        detail: {
          stepId: id,
          stepRevision: current.approval.stepRevision,
          draftRevision: current.approval.draftRevision,
        },
      });
      continue;
    }
    if (revised && (current.state === "pending" || current.state === prior.state)) {
      if (
        projection &&
        current.input.kind === "reply" &&
        prior.input.kind === "reply" &&
        current.input.draftRevision !== prior.input.draftRevision
      ) {
        const generated = proposalEvent(after, current, projection, base);
        if (generated) events.push(generated);
      } else if (
        current.input.kind === "reply" &&
        prior.input.kind === "reply" &&
        current.input.draftRevision !== prior.input.draftRevision
      )
        events.push({
          ...base(id, "draft.edited", {
            key: factKeys.draft(current.input.draftRevision, "draft.edited"),
            evidence: {
              kind: "draft_revision",
              ref: `${after.id}:r${current.input.draftRevision}`,
            },
          }),
          kind: "draft.edited",
          detail: { stepId: id, draftRevision: current.input.draftRevision },
        });
      else
        events.push({
          ...base(id, "step.revised"),
          kind: "step.revised",
          detail: { stepId: id, stepRevision: current.revision },
        });
      continue;
    }
    switch (current.state) {
      case "pending":
        events.push({
          ...base(id, "step.reopened"),
          kind: "step.reopened",
          detail: { stepId: id },
        });
        break;
      case "skipped":
        events.push({ ...base(id, "step.skipped"), kind: "step.skipped", detail: { stepId: id } });
        break;
      case "queued":
        events.push({
          ...base(id, "step.queued", { key: factKeys.effect(requireOperation(current), "queued") }),
          kind: "step.queued",
          detail: { stepId: id, operationId: requireOperation(current) },
        });
        break;
      case "executing":
        events.push({
          ...base(id, "effect.started", {
            key: factKeys.effect(requireOperation(current), "started"),
            actor: { type: "system", id: "outbox" },
          }),
          kind: "effect.started",
          detail: { stepId: id, operationId: requireOperation(current) },
        });
        break;
      case "uncertain":
        events.push({
          ...base(id, "effect.uncertain", {
            key: factKeys.effect(requireOperation(current), "uncertain"),
            actor: { type: "system", id: "outbox" },
          }),
          kind: "effect.uncertain",
          detail: { stepId: id, operationId: requireOperation(current), failureCode: null },
        });
        break;
      case "invalidated":
        events.push({
          ...base(id, "step.invalidated", { actor: { type: "system", id: null } }),
          kind: "step.invalidated",
          detail: { stepId: id, cause: cause.invalidation },
        });
        break;
      case "completed":
      case "reported": {
        const entry = appendedHistory.find((candidate) => candidate.stepId === id);
        if (!entry) throw new Error("unrecorded_completion");
        events.push(completionEvent(before, after, current, entry.evidence, entry.at, base));
        break;
      }
      case "approved":
        // Approved without a new approval: no transition produces this, so it is not recordable.
        throw new Error("unrecorded_transition");
    }
  }
  for (const removed of previousSteps.values())
    if (!SETTLED.has(removed.state))
      events.push({
        ...base(removed.id, "step.invalidated", { actor: { type: "system", id: null } }),
        kind: "step.invalidated",
        detail: {
          stepId: removed.id,
          cause: projection?.removed ?? `${cause.invalidation}:removed`,
        },
      });

  for (const entry of after.operatorOutcomeHistory.slice(
    before?.operatorOutcomeHistory.length ?? 0,
  ))
    events.push({
      ...base(entry.decisionId, "outcome.reviewed", {
        key: factKeys.outcome(entry.decisionId),
        actor: { type: "rep", id: entry.actorId },
        occurredAt: entry.reviewedAt,
      }),
      kind: "outcome.reviewed",
      detail: { kind: entry.kind },
    });
  for (const completion of after.goalCompletionHistory.slice(
    before?.goalCompletionHistory.length ?? 0,
  ))
    events.push(goalCompletedEvent(completion));
  for (const correction of after.goalCompletionCorrections.slice(
    before?.goalCompletionCorrections.length ?? 0,
  ))
    events.push(goalCorrectionEvent(correction));
  const previousPromises = new Map(
    (before?.followUpPromises ?? []).map((promise) => [promise.id, promise]),
  );
  for (const promise of after.followUpPromises) {
    const prior = previousPromises.get(promise.id);
    if (!prior)
      events.push({
        ...base(promise.id, "promise.created", {
          key: factKeys.promise(promise.id, "created"),
          actor: { type: "rep", id: promise.authorizedBy },
          occurredAt: promise.authorizedAt,
        }),
        kind: "promise.created",
        detail: {
          promiseId: promise.id,
          questionKey: promise.questionKey,
          question: promise.question,
          topic: promise.topic,
          checker: promise.checker,
          followUpOn: promise.followUpOn,
          commitmentPhrase: promise.commitmentPhrase,
        },
      });
    else if (prior.state !== promise.state && promise.state === "answer_ready")
      events.push({
        ...base(promise.id, "promise.answer_ready", {
          key: factKeys.promise(promise.id, "answer-ready"),
          occurredAt: promise.answeredAt ?? cause.occurredAt,
        }),
        kind: "promise.answer_ready",
        detail: { promiseId: promise.id },
      });
    else if (prior.state !== promise.state && promise.state === "kept")
      events.push({
        ...base(promise.id, "promise.kept", {
          key: factKeys.promise(promise.id, "kept"),
          occurredAt: promise.keptAt ?? cause.occurredAt,
        }),
        kind: "promise.kept",
        detail: { promiseId: promise.id },
      });
  }
  const appendedSends = after.sentRevisions.length - (before?.sentRevisions.length ?? 0);
  if (appendedSends !== events.filter((entry) => entry.kind === "reply.reported_sent").length)
    throw new Error("unrecorded_sent_revision");
  if (before ? before.disposition !== after.disposition : after.disposition !== "active")
    events.push({
      ...base(after.disposition, "disposition.set", { actor: { type: "system", id: null } }),
      kind: "disposition.set",
      // A drafting hold names the switch level that held it, which the Activity label shows.
      detail: {
        disposition: after.disposition,
        reason: after.disposition === "drafting_off" ? after.draftingHeldBy : null,
      },
    });
  return events.map((candidate) => ConversationEventSchema.parse(candidate));
}

type EventOverrides = Partial<Pick<ConversationEvent, "actor" | "occurredAt" | "evidence">> & {
  key?: string;
};
type EventBase = (
  subject: string,
  kind: string,
  overrides?: EventOverrides,
) => Omit<ConversationEvent, "kind" | "detail">;

/**
 * What a projection's new step says happened. A question the run needs answered and a draft it
 * generated are events; other proposals are shown from the plan as what comes next, and become
 * events when someone acts on them.
 */
function proposalEvent(
  plan: WorkPlan,
  step: PlanStep,
  projection: ProjectionContext,
  base: EventBase,
): ConversationEvent | null {
  if (step.input.kind === "question")
    return {
      ...base(step.id, "question.asked", { actor: { type: "system", id: null } }),
      kind: "question.asked",
      detail: { stepId: step.id, keys: step.input.questions.map((question) => question.key) },
    };
  if (step.input.kind === "reply" && projection.runId !== null)
    return {
      ...base(`${step.id}:r${step.input.draftRevision}`, "draft.generated", {
        key: factKeys.draft(step.input.draftRevision, "draft.generated"),
        actor: { type: "agent", id: "writer" },
        evidence: { kind: "draft_revision", ref: `${plan.id}:r${step.input.draftRevision}` },
      }),
      kind: "draft.generated",
      detail: {
        draftRevision: step.input.draftRevision,
        runId: projection.runId,
        answersMessageId: projection.answersMessageId,
      },
    };
  return null;
}

/** The sent revision this change recorded for a reply step, or null when it recorded none. */
function sentNow(before: WorkPlan | null, after: WorkPlan, step: PlanStep) {
  if (step.input.kind !== "reply") return null;
  const draftRevision = step.input.draftRevision;
  if (before?.sentRevisions.some((entry) => entry.draftRevision === draftRevision)) return null;
  return after.sentRevisions.find((entry) => entry.draftRevision === draftRevision) ?? null;
}

function requireOperation(step: PlanStep): string {
  if (step.operationId === null) throw new Error("unrecorded_operation");
  return step.operationId;
}

/**
 * A finished step, told apart by what proved it. A reply reported sent names its draft revision, and
 * so does a reply the app sent on approval: both record the revision now in the lead's mailbox, the
 * one fact that stops drafting proposing a replacement.
 */
function completionEvent(
  before: WorkPlan | null,
  plan: WorkPlan,
  step: PlanStep,
  proof: StepEvidence,
  at: string,
  base: EventBase,
): ConversationEvent {
  switch (proof.kind) {
    case "answer":
      return {
        ...base(step.id, "question.answered", {
          key: factKeys.completion(step.id, at, "question.answered"),
          actor: { type: "rep", id: proof.actorId },
          occurredAt: at,
        }),
        kind: "question.answered",
        detail: { stepId: step.id, uploadIds: proof.uploadIds },
      };
    case "provider_receipt": {
      const sent = sentNow(before, plan, step);
      // The app sent this reply (send-on-approve) and this receipt recorded it. Bison performed the
      // send, as it performs a connector effect, so the provider is the actor and the send stays in
      // the story rather than folding into housekeeping. A receipt for a reply the rep already
      // reported sent records nothing new, so it stays a plain completion.
      if (sent)
        return {
          ...base(step.id, "reply.reported_sent", {
            key: factKeys.sent(sent.decisionId),
            actor: { type: "provider", id: "bison" },
            occurredAt: sent.reportedAt,
            evidence: { kind: "provider_receipt", ref: proof.operationId },
          }),
          kind: "reply.reported_sent",
          detail: { stepId: step.id, draftRevision: sent.draftRevision },
        };
      if (step.input.kind === "connector")
        return {
          ...base(step.id, "effect.executed", {
            key: factKeys.effect(proof.operationId, "executed"),
            actor: { type: "provider", id: "bison" },
            occurredAt: proof.observedAt,
            evidence: { kind: "provider_receipt", ref: proof.operationId },
          }),
          kind: "effect.executed",
          detail: {
            stepId: step.id,
            operationId: proof.operationId,
            providerId: proof.providerId,
          },
        };
      return {
        ...base(step.id, "step.completed", {
          key: factKeys.completion(step.id, at, "step.completed"),
          actor: { type: "provider", id: null },
          occurredAt: proof.observedAt,
          evidence: { kind: "provider_receipt", ref: proof.operationId },
        }),
        kind: "step.completed",
        detail: { stepId: step.id, evidence: "provider_receipt" },
      };
    }
    case "rep_claim": {
      if (step.input.kind === "reply") {
        const draftRevision = step.input.draftRevision;
        const sent = plan.sentRevisions.find((entry) => entry.draftRevision === draftRevision);
        if (!sent) throw new Error("unrecorded_completion");
        return {
          ...base(step.id, "reply.reported_sent", {
            key: factKeys.sent(sent.decisionId),
            actor: { type: "rep", id: sent.actorId },
            occurredAt: sent.reportedAt,
          }),
          kind: "reply.reported_sent",
          detail: { stepId: step.id, draftRevision },
        };
      }
      return {
        ...base(step.id, "step.completed", {
          key: factKeys.completion(step.id, at, "step.completed"),
          actor: { type: "rep", id: proof.actorId },
          occurredAt: at,
        }),
        kind: "step.completed",
        detail: { stepId: step.id, evidence: "rep_claim" },
      };
    }
    case "clock":
      return {
        ...base(step.id, "step.completed", {
          key: factKeys.completion(step.id, at, "step.completed"),
          actor: { type: "system", id: "clock" },
          occurredAt: proof.observedAt,
        }),
        kind: "step.completed",
        detail: { stepId: step.id, evidence: "clock" },
      };
  }
}
